import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enableDesktopNotifications, pushSupported, syncPushSubscription } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { BellRing, X } from "lucide-react";

export const BLOCKED_STEPS = [
  ["Chrome", "Click the lock icon left of the address bar → Site settings → Notifications → Allow, then reload."],
  ["Edge", "Click the lock icon → Permissions for this site → Notifications → Allow, then reload."],
  ["Firefox", "Click the lock icon → Connection secure → More information → Permissions → Send notifications → Allow."],
];

function EnableBanner({ permission, onEnable, onHide }) {
  return (
    <div className="fixed bottom-4 left-1/2 z-50 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-[#2f8f86]/40 bg-[#16393f] p-4 text-sm text-teal-50 shadow-xl" data-testid="desktop-notify-banner">
      <div className="flex items-start gap-3">
        <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-teal-300" />
        <div className="flex-1">
          {permission === "denied" ? (
            <>
              <div className="font-semibold">Desktop notifications are blocked</div>
              <ul className="mt-1 space-y-0.5 text-xs text-teal-100/80">
                {BLOCKED_STEPS.map(([browser, step]) => <li key={browser}><b>{browser}:</b> {step}</li>)}
              </ul>
              <div className="mt-1 text-xs text-teal-100/70">Until then, alerts also arrive by email and in the bell.</div>
            </>
          ) : (
            <>
              <div className="font-semibold">Enable desktop notifications</div>
              <div className="text-xs text-teal-100/80">Get break, request and announcement alerts even when this tab is in the background.</div>
              <Button size="sm" onClick={onEnable} className="mt-2 bg-[#2f8f86] text-white hover:bg-[#287b73]" data-testid="desktop-notify-enable">Enable now</Button>
            </>
          )}
        </div>
        <button type="button" onClick={onHide} aria-label="Hide" data-testid="desktop-notify-hide"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function DesktopNotificationBridge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const seen = useRef(null);
  const [permission, setPermission] = useState(pushSupported() ? Notification.permission : "unsupported");
  const [hidden, setHidden] = useState(false);
  const pushActive = useRef(false);

  useEffect(() => {
    if (user && permission === "granted") {
      syncPushSubscription().then((ok) => { pushActive.current = ok; }).catch(() => {});
    }
  }, [user, permission]);

  useEffect(() => {
    if (!user) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/notify/items", { params: { unread_only: true, limit: 20 } });
        const fresh = (data.items || []).filter((n) => seen.current && !seen.current.has(n.id));
        seen.current = new Set([...(seen.current || []), ...(data.items || []).map((n) => n.id)]);
        if (!pushActive.current && document.visibilityState === "visible" && window.Notification?.permission === "granted") {
          fresh.slice(0, 3).forEach((n) => {
            const note = new Notification(n.title || "Nexreq", { body: n.body || "", tag: n.id });
            note.onclick = () => { window.focus(); navigate(n.link || "/"); note.close(); };
          });
        }
      } catch {}
      if (!stop) setTimeout(tick, 15000);
    };
    tick();
    return () => { stop = true; };
  }, [user, navigate]);

  if (!user || hidden || permission === "granted" || permission === "unsupported") return null;
  return (
    <EnableBanner
      permission={permission}
      onHide={() => setHidden(true)}
      onEnable={async () => setPermission(await enableDesktopNotifications())}
    />
  );
}
